import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { Button, InlineNotification, PasswordInput } from '@carbon/react';
import { useAppSelector } from '../../../app/hooks';
import { selectMe, selectToken } from '../../../AppSlice';
import { endpoints, GatewayIp } from '../../../data/endpoints';

const AccountSettingsTab: React.FC = () => {
  const token = useAppSelector(selectToken);
  const me = useAppSelector(selectMe);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const validationError = useMemo(() => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      return null;
    }
    if (newPassword !== confirmPassword) {
      return 'The new password and confirmation do not match.';
    }
    if (newPassword === currentPassword) {
      return 'The new password must be different from the current password.';
    }
    return null;
  }, [confirmPassword, currentPassword, newPassword]);

  const isSubmitDisabled =
    submitting ||
    !currentPassword ||
    !newPassword ||
    !confirmPassword ||
    !!validationError;

  const handleSubmit = async () => {
    setError(null);
    setSuccess(null);

    if (validationError) {
      setError(validationError);
      return;
    }

    if (!token) {
      setError('Authentication token not available.');
      return;
    }

    setSubmitting(true);

    try {
      await axios.put(
        GatewayIp + endpoints.auth.changePassword,
        {
          currentPassword,
          newPassword,
        },
        {
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setSuccess('Password changed successfully.');
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message ?? 'Failed to change password.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="settings-page__panel settings-page__account-panel" style={{ minWidth: 600 }}>
      <div className="settings-page__header">
        <h2>Account Settings</h2>
        <p>Signed in as {me?.username ?? me?.name ?? 'current user'}.</p>
      </div>

      {error && (
        <InlineNotification
          kind="error"
          lowContrast
          hideCloseButton
          subtitle={error}
          title="Password update failed"
        />
      )}

      {success && (
        <InlineNotification
          kind="success"
          lowContrast
          hideCloseButton
          subtitle={success}
          title="Password updated"
        />
      )}

      <div className="settings-page__form-grid">
        <PasswordInput
          id="current-password"
          labelText="Current password"
          value={currentPassword}
          onChange={(event) => setCurrentPassword(event.target.value)}
        />
        <PasswordInput
          id="new-password"
          labelText="New password"
          value={newPassword}
          onChange={(event) => setNewPassword(event.target.value)}
        />
        <PasswordInput
          id="confirm-password"
          labelText="Confirm new password"
          value={confirmPassword}
          onChange={(event) => setConfirmPassword(event.target.value)}
        />
      </div>

      <div className="settings-page__actions">
        <Button kind="primary" onClick={handleSubmit} disabled={isSubmitDisabled}>
          {submitting ? 'Updating password...' : 'Change password'}
        </Button>
      </div>
    </div>
  );
};

export default AccountSettingsTab;
